import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import * as pdfjsLib from 'pdfjs-dist';
import { EventBus, PDFViewer, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';
import './phpCsReferralViewer.css';
import { FormSubmission } from '../../types';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';
const TEMPLATE_URL = `${API_BASE_URL}/forms/templates/ecm-referral-child-youth`;

const EcmReferralChildYouthSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<PDFViewer | null>(null);
  const { setValue, getValues } = useFormContext<FormSubmission>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Text fields render as <input> when single-line and <textarea> when the
  // PDF field is multiline (e.g. the additional-comments field on page 5).
  const queryFormWidgets = (root: HTMLElement) =>
    root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[name], textarea[name]');

  // Read every rendered AcroForm widget from the DOM, keyed by its exact
  // PDF field name, and store it on the shared form state.
  const collectFromViewer = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;

    const flat: Record<string, string> = {};

    queryFormWidgets(root).forEach((el) => {
      const name = el.name;
      if (!name) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        const exportValue = el.getAttribute('exportValue') || 'On';
        flat[name] = el.checked ? `/${exportValue}` : '/Off';
      } else {
        flat[name] = el.value ?? '';
      }
    });

    setValue('ecmReferralChildYouth_flat', flat, { shouldDirty: true });
  }, [setValue]);

  // Restore any previously saved values (e.g. navigating back to this step).
  // Safe to call repeatedly as more pages' widgets come online — it only
  // ever touches widgets that already exist in the DOM.
  const applySavedValues = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;

    const saved = getValues('ecmReferralChildYouth_flat');
    if (!saved) return;

    queryFormWidgets(root).forEach((el) => {
      const value = saved[el.name];
      if (value === undefined) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        const exportValue = el.getAttribute('exportValue') || 'On';
        el.checked = value === `/${exportValue}`;
      } else {
        el.value = value;
      }
    });
  }, [getValues]);

  useEffect(() => {
    let cancelled = false;
    let pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
    const container = containerRef.current;
    if (!container) return;

    // Reset the container in case a previous instance (e.g. React StrictMode's
    // extra dev-mode mount/unmount cycle) left DOM behind, then give
    // PDFViewer the empty .pdfViewer div it requires as its first child.
    container.innerHTML = '';
    const viewerDiv = document.createElement('div');
    viewerDiv.className = 'pdfViewer';
    container.appendChild(viewerDiv);

    const eventBus = new EventBus();
    const linkService = new PDFLinkService({ eventBus });
    const viewer = new PDFViewer({
      container,
      eventBus,
      linkService,
      annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
    });
    linkService.setViewer(viewer);
    viewerRef.current = viewer;

    const onFieldChange = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        collectFromViewer();
      }
    };
    container.addEventListener('input', onFieldChange, true);
    container.addEventListener('change', onFieldChange, true);

    // "pagesloaded" only means page canvas/layout is ready — the actual
    // AcroForm <input>/<textarea> widgets are built by each page's annotation
    // layer, which renders asynchronously and fires its own event per page.
    // With 6 pages, this fires once per page as it comes online. We only ever
    // *apply* saved values here (safe to repeat — it only touches widgets that
    // already exist). We deliberately do NOT collect here: with pages 2-6 not
    // yet rendered, a collect would overwrite the shared flat map with only
    // page 1's widgets and permanently wipe out pages 2-6 (e.g. right after
    // reopening an existing submission for edit, before the user has touched
    // anything). Collection only happens in response to real user input.
    const onAnnotationLayerRendered = () => {
      if (cancelled) return;
      applySavedValues();
      setStatus('ready');
    };
    eventBus.on('annotationlayerrendered', onAnnotationLayerRendered);

    pdfjsLib.getDocument(TEMPLATE_URL).promise
      .then((doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        pdfDocument = doc;
        linkService.setDocument(doc, null);
        viewer.setDocument(doc);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      container.removeEventListener('input', onFieldChange, true);
      container.removeEventListener('change', onFieldChange, true);
      eventBus.off('annotationlayerrendered', onAnnotationLayerRendered);
      viewer.cleanup();
      pdfDocument?.destroy();
      container.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllFields = () => {
    const root = containerRef.current;
    if (!root) return;
    queryFormWidgets(root).forEach((el) => {
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = false;
      else el.value = '';
    });
    collectFromViewer();
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Fill out the referral form below by typing directly into its fields. Your entries are saved automatically as you type.
      </div>

      {status === 'error' && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Failed to load the referral form PDF. Please refresh and try again.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={clearAllFields}
          className="text-sm text-gray-600 hover:text-gray-900 underline"
        >
          Clear all fields
        </button>
      </div>

      <div
        className="relative border border-gray-300 rounded overflow-auto bg-gray-100"
        style={{ height: '80vh' }}
      >
        <div ref={containerRef} className="absolute inset-0 overflow-auto" />
      </div>
    </div>
  );
};

export default EcmReferralChildYouthSection;
